import { useEffect, useState } from "react";
import { Copy, RefreshCw } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { del, get, post } from "../api/client";
import { keys, useUpdateSettings } from "../api/hooks";
import type { Me, Settings, ViewName } from "../api/types";
import { currentPushSubscription, disablePush, enablePush, pushSupported } from "../lib/push";
import { reminderLabel } from "./overlays/labels";
import { useUi } from "../state/ui";
import { VIEW_LABELS } from "../lib/views";
import "./SettingsPage.css";

const TIMEZONES = [
  "America/Sao_Paulo",
  "America/Manaus",
  "America/Cuiaba",
  "America/Belem",
  "America/Fortaleza",
  "America/Recife",
  "America/Bahia",
  "America/Porto_Velho",
  "America/Boa_Vista",
  "America/Rio_Branco",
  "America/Noronha",
  "America/New_York",
  "Europe/Lisbon",
  "Europe/London",
  "UTC"
];

const REMINDER_OPTIONS = [0, 5, 10, 15, 30, 60, 1440];

function Section({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    <section id={id} className="st-section">
      <h2>{title}</h2>
      {children}
    </section>
  );
}

export function SettingsPage() {
  const ui = useUi();
  const qc = useQueryClient();
  const update = useUpdateSettings();
  const s = ui.me.settings;
  const set = (changes: Partial<Settings> & { timezone?: string }) => update.mutate(changes, { onSuccess: () => ui.showToast("Configuração salva") });
  const [push, setPush] = useState<"on" | "off" | "denied" | "unsupported" | "loading">("loading");

  useEffect(() => {
    if (!pushSupported()) setPush("unsupported");
    else if (Notification.permission === "denied") setPush("denied");
    else currentPushSubscription().then((sub) => setPush(sub ? "on" : "off"));
  }, []);

  const sections: Array<[string, string]> = [
    ["geral", "Geral"],
    ["visualizacao", "Visualização"],
    ["notificacoes", "Notificações"],
    ["integrar", "Integrar agenda"],
    ["conta", "Conta"]
  ];

  const tzOptions = TIMEZONES.includes(ui.me.timezone) ? TIMEZONES : [ui.me.timezone, ...TIMEZONES];

  return (
    <div className="st">
      <nav className="st-nav" aria-label="Seções">
        {sections.map(([id, label]) => (
          <a key={id} href={`#${id}`} className="st-nav-item" onClick={(e) => { e.preventDefault(); document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }); }}>
            {label}
          </a>
        ))}
      </nav>
      <div className="st-content">
        <Section id="geral" title="Geral">
          <label className="st-field">
            <span>Fuso horário</span>
            <select className="chip-select" value={ui.me.timezone} onChange={(e) => set({ timezone: e.target.value })}>
              {tzOptions.map((tz) => (
                <option key={tz} value={tz}>
                  {tz.replace("_", " ")}
                </option>
              ))}
            </select>
          </label>
          <label className="st-field">
            <span>Tema</span>
            <select className="chip-select" value={s.theme} onChange={(e) => set({ theme: e.target.value as Settings["theme"] })}>
              <option value="system">Padrão do dispositivo</option>
              <option value="light">Claro</option>
              <option value="dark">Escuro</option>
            </select>
          </label>
        </Section>

        <Section id="visualizacao" title="Visualização">
          <label className="st-field">
            <span>Começar a semana em</span>
            <select className="chip-select" value={s.weekStart} onChange={(e) => set({ weekStart: Number(e.target.value) as Settings["weekStart"] })}>
              <option value={0}>Domingo</option>
              <option value={1}>Segunda-feira</option>
              <option value={6}>Sábado</option>
            </select>
          </label>
          <label className="st-field">
            <span>Visualização padrão</span>
            <select className="chip-select" value={s.defaultView} onChange={(e) => set({ defaultView: e.target.value as ViewName })}>
              {(Object.keys(VIEW_LABELS) as ViewName[]).map((v) => (
                <option key={v} value={v}>
                  {VIEW_LABELS[v].label}
                </option>
              ))}
            </select>
          </label>
          <label className="st-field">
            <span>Duração padrão dos eventos</span>
            <select className="chip-select" value={s.defaultDurationMinutes} onChange={(e) => set({ defaultDurationMinutes: Number(e.target.value) })}>
              {[15, 30, 45, 60, 90, 120].map((m) => (
                <option key={m} value={m}>
                  {m < 60 ? `${m} minutos` : `${m / 60} ${m === 60 ? "hora" : "horas"}`.replace("1.5 horas", "1h30")}
                </option>
              ))}
            </select>
          </label>
          <label className="checkbox st-check">
            <input type="checkbox" checked={s.showWeekends} onChange={(e) => set({ showWeekends: e.target.checked })} />
            Mostrar fins de semana
          </label>
          <label className="checkbox st-check">
            <input type="checkbox" checked={s.showHolidays} onChange={(e) => set({ showHolidays: e.target.checked })} />
            Mostrar feriados no Brasil
          </label>
        </Section>

        <Section id="notificacoes" title="Notificações">
          <p className="st-help">Os lembretes chegam na hora certa pelos canais ativados abaixo.</p>
          <label className="checkbox st-check">
            <input type="checkbox" checked={s.notifyWhatsapp} onChange={(e) => set({ notifyWhatsapp: e.target.checked })} />
            Receber lembretes pelo WhatsApp
          </label>
          <label className="checkbox st-check">
            <input type="checkbox" checked={s.notifyPush} onChange={(e) => set({ notifyPush: e.target.checked })} />
            Receber notificações no navegador / app instalado
          </label>
          <div className="st-row">
            {push === "unsupported" && <span className="st-help">Este navegador não suporta notificações push.</span>}
            {push === "denied" && <span className="st-help">As notificações estão bloqueadas nas permissões do navegador para este site.</span>}
            {push === "off" && (
              <button
                className="btn outline"
                onClick={async () => {
                  const result = await enablePush(ui.me.vapidPublicKey);
                  setPush(result === "granted" ? "on" : result === "denied" ? "denied" : "unsupported");
                  if (result === "granted") ui.showToast("Notificações ativadas neste dispositivo");
                }}
              >
                Ativar notificações neste dispositivo
              </button>
            )}
            {push === "on" && (
              <>
                <span className="st-help">Ativadas neste dispositivo.</span>
                <button className="btn text" onClick={() => post<{ delivered: number }>("/api/v1/push/test").then((r) => ui.showToast(r.delivered ? "Notificação de teste enviada" : "Nenhum dispositivo recebeu"))}>
                  Enviar teste
                </button>
                <button className="btn text danger" onClick={async () => { await disablePush(); setPush("off"); }}>
                  Desativar
                </button>
              </>
            )}
          </div>

          <h3>Resumo do dia no WhatsApp</h3>
          <p className="st-help">Agenda do dia, pendências, contas que vencem e previsão do ciclo, numa mensagem só.</p>
          <div className="st-field">
            <span>Enviar</span>
            <span className="st-row">
              <select
                className="chip-select"
                aria-label="Resumo do dia"
                value={s.dailyDigest && s.dailyDigest !== "off" ? "on" : "off"}
                disabled={!s.notifyWhatsapp}
                onChange={(e) => set({ dailyDigest: e.target.value === "on" ? "07:00" : "off" })}
              >
                <option value="on">Todo dia às</option>
                <option value="off">Não enviar</option>
              </select>
              {s.dailyDigest && s.dailyDigest !== "off" && (
                <input
                  className="field filled digest-time"
                  type="time"
                  aria-label="Horário do resumo"
                  value={s.dailyDigest}
                  disabled={!s.notifyWhatsapp}
                  onChange={(e) => /^\d\d:\d\d$/.test(e.target.value) && set({ dailyDigest: e.target.value })}
                />
              )}
            </span>
          </div>
          {s.dailyDigest === null && <p className="st-help">Ainda não escolhido — o assistente pergunta na sua próxima conversa pelo WhatsApp.</p>}
          <label className="checkbox st-check">
            <input type="checkbox" checked={s.dayReview} disabled={!s.notifyWhatsapp} onChange={(e) => set({ dayReview: e.target.checked })} />
            No fim do último compromisso do dia, perguntar o que foi concluído (enquete)
          </label>

          <h3>Notificações padrão de eventos</h3>
          <ReminderDefaults value={s.defaultReminders} allDay={false} onChange={(v) => set({ defaultReminders: v })} />
          <h3>Notificações padrão de eventos de dia inteiro</h3>
          <ReminderDefaults value={s.defaultAllDayReminders} allDay onChange={(v) => set({ defaultAllDayReminders: v })} />
        </Section>

        <Section id="integrar" title="Integrar agenda">
          <GoogleSync />
          <h3>Assinar por endereço iCal</h3>
          <p className="st-help">
            Endereço secreto no formato iCal: assine no app de calendário do celular (Google Agenda: “Outras agendas → Do URL”; iPhone: Ajustes → Calendário → Contas →
            “Adicionar calendário assinado”). É somente leitura. Não compartilhe este endereço.
          </p>
          <IcsUrl me={ui.me} onRotated={() => qc.invalidateQueries({ queryKey: keys.me })} />
        </Section>

        <Section id="conta" title="Conta">
          <p className="st-help">
            Conectado como <strong>{ui.me.name ?? "você"}</strong> ({ui.me.phone}).
          </p>
          <button
            className="btn outline"
            onClick={async () => {
              await post("/api/v1/auth/logout");
              window.location.href = "/entrar";
            }}
          >
            Sair
          </button>
        </Section>
      </div>
    </div>
  );
}

function ReminderDefaults({ value, allDay, onChange }: { value: number[]; allDay: boolean; onChange: (v: number[]) => void }) {
  const options = allDay ? [0, 1440, 2880, 10080] : REMINDER_OPTIONS;
  return (
    <div className="st-reminders">
      {value.map((m, i) => (
        <div key={`${m}-${i}`} className="st-row">
          <select className="chip-select" value={m} onChange={(e) => onChange(value.map((x, j) => (j === i ? Number(e.target.value) : x)))}>
            {[...new Set([...options, m])].sort((a, b) => a - b).map((o) => (
              <option key={o} value={o}>
                {reminderLabel(o, allDay)}
              </option>
            ))}
          </select>
          <button className="btn text" onClick={() => onChange(value.filter((_, j) => j !== i))}>
            Remover
          </button>
        </div>
      ))}
      {value.length < 5 && (
        <button className="btn text" onClick={() => onChange([...value, options.find((o) => !value.includes(o)) ?? 0])}>
          Adicionar notificação
        </button>
      )}
    </div>
  );
}

/** Google Agenda mirror: every event is also written to the user's Google Agenda. */
function GoogleSync() {
  const ui = useUi();
  const qc = useQueryClient();
  const status = useQuery({
    queryKey: ["integrations", "google"],
    queryFn: () => get<{ available: boolean; connected: boolean; needsReconnect?: boolean }>("/api/v1/integrations/google")
  });
  const connect = async () => {
    const { url } = await post<{ url: string }>("/api/v1/integrations/google/connect");
    window.location.href = url;
  };
  const refresh = () => qc.invalidateQueries({ queryKey: ["integrations", "google"] });

  // Back from Google's consent screen (the OAuth callback redirects here).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("google") !== "conectado") return;
    ui.showToast("Google Agenda conectado. Copiando seus compromissos…");
    params.delete("google");
    const qs = params.toString();
    window.history.replaceState(null, "", window.location.pathname + (qs ? `?${qs}` : ""));
    document.getElementById("integrar")?.scrollIntoView();
  }, []);

  if (!status.data?.available) return null;
  return (
    <>
      <h3>Google Agenda</h3>
      {status.data.needsReconnect ? (
        <>
          <p className="st-help st-alert">
            A conexão com o Google expirou: o Google deixou de aceitar a autorização. Seus compromissos continuam salvos aqui; ao reconectar, tudo o que ficou pendente é
            enviado.
          </p>
          <div className="st-row">
            <button className="btn primary" onClick={connect}>
              Reconectar Google Agenda
            </button>
          </div>
        </>
      ) : status.data.connected ? (
        <>
          <p className="st-help">
            Conectado. Tudo o que você marcar aqui (ou pelo WhatsApp) também é criado, alterado e excluído no seu Google Agenda. Alterações feitas direto no Google não voltam
            para cá.
          </p>
          <div className="st-row">
            <button className="btn outline" onClick={() => post("/api/v1/integrations/google/sync").then(() => ui.showToast("Sincronizando todos os compromissos com o Google"))}>
              Sincronizar tudo agora
            </button>
            <button
              className="btn text danger"
              onClick={async () => {
                if (!window.confirm("Desconectar o Google Agenda? Os eventos já copiados continuam lá, mas novas alterações não serão mais enviadas.")) return;
                await del("/api/v1/integrations/google");
                refresh();
                ui.showToast("Google Agenda desconectado");
              }}
            >
              Desconectar
            </button>
          </div>
        </>
      ) : (
        <>
          <p className="st-help">Conecte sua conta Google para ter todos os compromissos também no Google Agenda, além da agenda própria.</p>
          <div className="st-row">
            <button className="btn primary" onClick={connect}>
              Conectar Google Agenda
            </button>
          </div>
        </>
      )}
    </>
  );
}

function IcsUrl({ me, onRotated }: { me: Me; onRotated: () => void }) {
  const ui = useUi();
  return (
    <div className="st-row">
      <input className="field filled st-ics" readOnly value={me.icsUrl} aria-label="Endereço iCal" onFocus={(e) => e.currentTarget.select()} />
      <button className="icon-btn" aria-label="Copiar endereço" title="Copiar" onClick={() => navigator.clipboard.writeText(me.icsUrl).then(() => ui.showToast("Endereço copiado"))}>
        <Copy size={18} />
      </button>
      <button
        className="icon-btn"
        aria-label="Gerar novo endereço"
        title="Gerar novo endereço (o antigo para de funcionar)"
        onClick={async () => {
          if (!window.confirm("Gerar um novo endereço? O endereço atual vai parar de funcionar nos apps onde foi assinado.")) return;
          await post("/api/v1/ics/rotate");
          onRotated();
          ui.showToast("Novo endereço gerado");
        }}
      >
        <RefreshCw size={18} />
      </button>
    </div>
  );
}
